import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PaginationInfo } from 'src/app/shared/models';
import { TestimonialService } from 'src/app/services/testimonial/testimonial.service';
import { TestimonialModalService } from 'src/app/services/testimonial-modal/testimonial-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-cm-testimonial',
  templateUrl: './cm-testimonial.component.html',
  styleUrls: ['./cm-testimonial.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0', opacity: 0, overflow: 'hidden' }),
        animate('300ms ease-in-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden' }),
        animate('300ms ease-in-out', style({ height: '0', opacity: 0 }))
      ])
    ])
  ]
})
export class CmTestimonialComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  testimonials: any[] = [];
  isLoading = false;

  // Pagination
  currentPage = 1;
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // Search and filters
  searchTerm = '';
  showFiltersForm = false;
  filters: Record<string, any> = {};

  // Bulk selection
  selectedTestimonials: string[] = [];

  constructor(
    private _testimonialService: TestimonialService,
    private _testimonialModalService: TestimonialModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadTestimonials();
    this.setupModalSubscriptions();
  }

  ngAfterViewInit(): void {
    this.initializeMenus();
  }

  trackByFn(index: number, item: any): any {
    return item._id || item.id || index;
  }

  private setupModalSubscriptions(): void {
    // Subscribe to modal service if needed
  }

  private initializeMenus(): void {
    setTimeout(() => {
      const menuElements = document.querySelectorAll('[data-kt-menu="true"]');
      menuElements.forEach((element: any) => {
        if (element && typeof element.KTMenu === 'undefined') {
          // Initialize menu if not already initialized
        }
      });
    }, 100);
  }

  loadTestimonials(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;
    this.currentPage = page;
    this.cdr.detectChanges();

    this.subscriptions.push(
      this._testimonialService.GetData(page.toString(), search, filters).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            this.testimonials = response.data.data || [];
            this.paginationInfo = {
              currentPage: response.data.current_page || 1,
              perPage: response.data.per_page || 10,
              total: response.data.total || 0,
              lastPage: response.data.last_page || 1,
              firstPage: 1
            };
          } else {
            this.testimonials = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars();
        },
        error: (error) => {
          console.error('Error loading testimonials:', error);
          this.testimonials = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars();
        }
      })
    );
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  onPageChange(page: number): void {
    this.loadTestimonials(page, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.loadTestimonials(1, this.searchTerm, this.filters);
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.filters = {};
    this.showFiltersForm = false;
    this.loadTestimonials();
  }

  openAddTestimonialModal(): void {
    this._testimonialModalService.clearSelectedTestimonial();
    const modalElement = document.getElementById('kt_modal_testimonial_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditModal(testimonial: any): void {
    this._testimonialModalService.setSelectedTestimonial(testimonial);
    const modalElement = document.getElementById('kt_modal_testimonial_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteTestimonial(testimonial: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(testimonial);
      }
    });
  }

  private performDelete(testimonial: any): void {
    const testimonialId = testimonial._id || testimonial.id;
    this.subscriptions.push(
      this._testimonialService.DeleteDataById(testimonialId).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Testimonial has been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadTestimonials(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete testimonial.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while deleting testimonial.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  // Event handlers for modal events
  onTestimonialAdded(): void {
    console.log('Testimonial added, refreshing list...');
    this.loadTestimonials(this.currentPage, this.searchTerm, this.filters);
  }

  onTestimonialUpdated(): void {
    console.log('Testimonial updated, refreshing list...');
    this.loadTestimonials(this.currentPage, this.searchTerm, this.filters);
  }

  // Bulk selection methods
  toggleSelection(testimonialId: string, event: any): void {
    if (event.target.checked) {
      if (!this.selectedTestimonials.includes(testimonialId)) {
        this.selectedTestimonials.push(testimonialId);
      }
    } else {
      this.selectedTestimonials = this.selectedTestimonials.filter(id => id !== testimonialId);
    }
    this.toggleToolbars();
  }

  toggleAllSelection(event: any): void {
    if (event.target.checked) {
      this.selectedTestimonials = this.testimonials.map(t => t._id || t.id);
    } else {
      this.selectedTestimonials = [];
    }
    this.toggleToolbars();
  }

  isSelected(testimonialId: string): boolean {
    return this.selectedTestimonials.includes(testimonialId);
  }

  isAllSelected(): boolean {
    return this.testimonials.length > 0 && this.selectedTestimonials.length === this.testimonials.length;
  }

  isIndeterminate(): boolean {
    return this.selectedTestimonials.length > 0 && this.selectedTestimonials.length < this.testimonials.length;
  }

  private toggleToolbars(): void {
    const baseToolbar = document.querySelector('[data-kt-testimonial-table-toolbar="base"]');
    const selectedToolbar = document.querySelector('[data-kt-testimonial-table-toolbar="selected"]');

    if (this.selectedTestimonials.length > 0) {
      baseToolbar?.classList.add('d-none');
      selectedToolbar?.classList.remove('d-none');
    } else {
      baseToolbar?.classList.remove('d-none');
      selectedToolbar?.classList.add('d-none');
    }
  }

  deleteSelectedTestimonials(): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete ${this.selectedTestimonials.length} testimonial(s). This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete them!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performBulkDelete();
      }
    });
  }

  private performBulkDelete(): void {
    this.subscriptions.push(
      this._testimonialService.BulkDelete(this.selectedTestimonials).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Selected testimonials have been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.selectedTestimonials = [];
              this.loadTestimonials(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete testimonials.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while deleting testimonials.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }
}
